// src/pages/client/AboutPage.jsx

import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  MapPin,
  Mail,
  Phone,
  ArrowRight,
  Heart,
  Target,
  Eye,
  Award,
  Users,
  Shield,
  Clock,
  Globe,
  ArrowLeft,
  Loader,
  AlertCircle,
  DollarSign,
  Briefcase,
  Star,
  CheckCircle,
  ThumbsUp
} from 'lucide-react';

// Redux imports
import { getPublicSettings, getAllSettings } from '../../store/slices/platformSettingsSlice';
import {
  selectPublicSettings,
  selectSettingsLoading,
  selectSettingsError
} from '../../store/slices/platformSettingsSlice';

import {
  selectAllProviders,
  selectProviderLoading,
  getAllProviders
} from '../../store/slices/providerSlice';

import {
  selectDonationGoals,
} from '../../store/slices/platformSettingsSlice';

// Import reviews selectors
import {
  selectReviews,
  selectReviewsLoading,
  selectReviewStatistics,
  selectRatingSummary,
  getReviews
} from '../../store/slices/serviceProviderReviewSlice';

// Custom hooks
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';

// Services
import customerSatisfactionService from '../../services/customerSatisfactionService';

// Styles
import styles from '../../styles/About.module.css';
import colors from '../../constants/colors';

const AboutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [satisfactionMetrics, setSatisfactionMetrics] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);

  // ======================================================
  // REDUX SELECTORS
  // ======================================================
  const publicSettings = useSelector(selectPublicSettings);
  const settingsLoading = useSelector(selectSettingsLoading);
  const settingsError = useSelector(selectSettingsError);
  
  const providers = useSelector(selectAllProviders);
  const providersLoading = useSelector(selectProviderLoading);
  
  const donationGoals = useSelector(selectDonationGoals);

  // Reviews for customer satisfaction
  const reviews = useSelector(selectReviews);
  const reviewsLoading = useSelector(selectReviewsLoading);
  const reviewStatistics = useSelector(selectReviewStatistics);
  const ratingSummary = useSelector(selectRatingSummary);

  // ======================================================
  // ANALYZE CUSTOMER SATISFACTION USING THE SERVICE
  // ======================================================
  useEffect(() => {
    // Only analyze if we have reviews data
    if (reviews && Array.isArray(reviews) && reviews.length > 0) {
      setIsAnalyzing(true);
      try {
        // Transform reviews to the format the service expects
        const formattedReviews = reviews.map(review => ({
          text: review.comment || review.text || review.reviewText || '',
          rating: review.rating || 0,
          createdAt: review.date || review.createdAt || new Date().toISOString(),
          _id: review._id,
          customerName: review.customerName || 'Anonymous'
        }));

        // Use the Customer Satisfaction Service
        const metrics = customerSatisfactionService.analyzeReviews(formattedReviews);
        setSatisfactionMetrics(metrics);
        setReviewsError(null);
      } catch (error) {
        console.error('Failed to analyze customer satisfaction:', error);
        setReviewsError('Failed to analyze reviews');
        
        // Fallback: Use review statistics if available
        if (reviewStatistics && typeof reviewStatistics === 'object') {
          const avgRating = reviewStatistics.averageRating || 0;
          const totalReviews = reviewStatistics.totalReviews || 0;
          const satisfactionRate = totalReviews > 0 ? Math.round((avgRating / 5) * 100) : 0;
          
          setSatisfactionMetrics({
            totalReviews: totalReviews,
            positiveCount: Math.round(totalReviews * (satisfactionRate / 100)),
            negativeCount: Math.round(totalReviews * ((100 - satisfactionRate) / 100)),
            neutralCount: 0,
            positivePercentage: satisfactionRate,
            negativePercentage: 100 - satisfactionRate,
            neutralPercentage: 0,
            averageScore: Math.round((avgRating / 5) * 100),
            satisfactionRate: satisfactionRate,
            overallSentiment: satisfactionRate >= 70 ? 'Excellent' : satisfactionRate >= 50 ? 'Good' : 'Average',
            summary: totalReviews > 0 
              ? `${satisfactionRate}% of customers are satisfied with our services.`
              : 'No reviews available to analyze.',
            metrics: {
              satisfaction: satisfactionRate,
              positive: satisfactionRate,
              negative: 100 - satisfactionRate,
              neutral: 0,
              averageScore: Math.round((avgRating / 5) * 100)
            },
            reviewBreakdown: {
              positive: Math.round(totalReviews * (satisfactionRate / 100)),
              negative: Math.round(totalReviews * ((100 - satisfactionRate) / 100)),
              neutral: 0
            },
            topPositiveKeywords: [],
            topNegativeKeywords: []
          });
        }
      } finally {
        setIsAnalyzing(false);
      }
    } else if (reviewStatistics && typeof reviewStatistics === 'object' && reviewStatistics.totalReviews > 0) {
      // We have statistics but no individual reviews
      const avgRating = reviewStatistics.averageRating || 0;
      const totalReviews = reviewStatistics.totalReviews || 0;
      const satisfactionRate = totalReviews > 0 ? Math.round((avgRating / 5) * 100) : 0;
      
      setSatisfactionMetrics({
        totalReviews: totalReviews,
        positiveCount: Math.round(totalReviews * (satisfactionRate / 100)),
        negativeCount: Math.round(totalReviews * ((100 - satisfactionRate) / 100)),
        neutralCount: 0,
        positivePercentage: satisfactionRate,
        negativePercentage: 100 - satisfactionRate,
        neutralPercentage: 0,
        averageScore: Math.round((avgRating / 5) * 100),
        satisfactionRate: satisfactionRate,
        overallSentiment: satisfactionRate >= 70 ? 'Excellent' : satisfactionRate >= 50 ? 'Good' : 'Average',
        summary: `${satisfactionRate}% of customers are satisfied with our services.`,
        metrics: {
          satisfaction: satisfactionRate,
          positive: satisfactionRate,
          negative: 100 - satisfactionRate,
          neutral: 0,
          averageScore: Math.round((avgRating / 5) * 100)
        },
        reviewBreakdown: {
          positive: Math.round(totalReviews * (satisfactionRate / 100)),
          negative: Math.round(totalReviews * ((100 - satisfactionRate) / 100)),
          neutral: 0
        },
        topPositiveKeywords: [],
        topNegativeKeywords: []
      });
      setIsAnalyzing(false);
    } else {
      // No reviews available
      setSatisfactionMetrics(customerSatisfactionService.getEmptyMetrics());
      setIsAnalyzing(false);
    }
  }, [reviews, reviewStatistics]);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: aboutDescription } = useTranslation('Haitian Services Platform is a premier digital marketplace connecting skilled Haitian professionals with clients worldwide. We empower Haitian entrepreneurs and service providers by providing a global platform to showcase their talents and grow their businesses.');
  const { translatedText: exploreProviders } = useTranslation('Explore Providers');
  const { translatedText: contactUs } = useTranslation('Contact Us');
  const { translatedText: ourMission } = useTranslation('Our Mission');
  const { translatedText: missionText } = useTranslation('To empower Haitian professionals and businesses by providing a world-class digital platform that connects them with global opportunities, fosters economic growth, and strengthens the Haitian diaspora community.');
  const { translatedText: ourVision } = useTranslation('Our Vision');
  const { translatedText: visionText } = useTranslation('To become the leading global marketplace for Haitian services, creating sustainable economic opportunities and building a bridge between Haiti and the world through innovation, trust, and excellence.');
  const { translatedText: ourCoreValues } = useTranslation('Our Core Values');
  const { translatedText: coreValuesSubtitle } = useTranslation('The principles that guide everything we do');
  const { translatedText: communityFirst } = useTranslation('Community First');
  const { translatedText: communityFirstDesc } = useTranslation('We believe in the power of community and strive to create meaningful connections that uplift and empower.');
  const { translatedText: empowerment } = useTranslation('Empowerment');
  const { translatedText: empowermentDesc } = useTranslation('We are committed to empowering Haitian professionals by providing the tools and platform they need to succeed.');
  const { translatedText: trustSafety } = useTranslation('Trust & Safety');
  const { translatedText: trustSafetyDesc } = useTranslation('We prioritize trust and safety, ensuring secure transactions and building lasting relationships.');
  const { translatedText: excellence } = useTranslation('Excellence');
  const { translatedText: excellenceDesc } = useTranslation('We hold ourselves to the highest standards of excellence and continuously innovate to serve our community better.');
  const { translatedText: whyChooseUs } = useTranslation('Why Choose Us');
  const { translatedText: globalReach } = useTranslation('Global Reach');
  const { translatedText: globalReachDesc } = useTranslation('Access a worldwide network of clients and partners.');
  const { translatedText: availability } = useTranslation('24/7 Availability');
  const { translatedText: availabilityDesc } = useTranslation('Connect and transact anytime, anywhere.');
  const { translatedText: communitySupport } = useTranslation('Community Support');
  const { translatedText: communitySupportDesc } = useTranslation('Join a vibrant community of professionals.');
  const { translatedText: getInTouch } = useTranslation('Get in Touch');
  const { translatedText: followUs } = useTranslation('Follow Us');
  const { translatedText: socialText } = useTranslation('Connect with us on social media to stay updated with the latest news and events.');
  const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide');
  const { translatedText: madeWithLove } = useTranslation('All rights reserved');
  const { translatedText: activeProviders } = useTranslation('Active Providers');
  const { translatedText: completedProjectsLabel } = useTranslation('Completed Projects');
  const { translatedText: clientSatisfaction } = useTranslation('Client Satisfaction');
  const { translatedText: countriesReached } = useTranslation('Countries Reached');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');
  const { translatedText: totalDonations } = useTranslation('Total Donations');
  const { translatedText: donationGoalsCount } = useTranslation('Donation Goals');

  // ======================================================
  // CALCULATE STATISTICS
  // ======================================================
  // Provider statistics
  const providerStats = useMemo(() => {
    if (!providers || !Array.isArray(providers)) {
      return {
        totalProviders: 0,
        countriesReached: 0
      };
    }

    const countries = new Set();
    providers.forEach(p => {
      const country = p.address?.country || p.country || 'Unknown';
      countries.add(country);
    });

    return {
      totalProviders: providers.length,
      countriesReached: countries.size
    };
  }, [providers]);

  // ======================================================
  // DONATION STATISTICS - Using donation history
  // ======================================================
  const donationStatsData = useMemo(() => {
    // First, check donation goals
    if (donationGoals && Array.isArray(donationGoals) && donationGoals.length > 0) {
      // Parse donation goals with proper number conversion
      const parsedGoals = donationGoals.map(goal => {
        const targetAmount = Number(goal.targetAmount) || 0;
        const currentAmount = Number(goal.currentAmount) || 0;
        
        const isCompleted = targetAmount > 0 && currentAmount >= targetAmount;
        
        return {
          ...goal,
          targetAmount,
          currentAmount,
          isCompleted,
          isActive: !isCompleted
        };
      });

      const completed = parsedGoals.filter(g => g.isCompleted);
      const active = parsedGoals.filter(g => !g.isCompleted);
      
      const totalTarget = parsedGoals.reduce((sum, g) => sum + g.targetAmount, 0);
      const totalCurrent = parsedGoals.reduce((sum, g) => sum + g.currentAmount, 0);
      const progress = totalTarget > 0 ? (totalCurrent / totalTarget) * 100 : 0;

      return {
        totalGoals: parsedGoals.length,
        activeGoals: active.length,
        completedGoals: completed.length,
        totalTargetAmount: totalTarget,
        totalCurrentAmount: totalCurrent,
        overallProgress: Math.round(progress)
      };
    }

    // If no donation goals, check if we have public donations to show as completed projects
    const publicDonations = publicSettings?.publicDonationIds || [];
    const completedDonations = publicDonations.filter(d => d.amount && d.amount > 0);
    
    // Calculate total from donations
    const totalDonationsAmount = completedDonations.reduce((sum, d) => sum + (d.amount || 0), 0);
    
    return {
      totalGoals: 0,
      activeGoals: 0,
      completedGoals: completedDonations.length > 0 ? 1 : 0,
      totalTargetAmount: totalDonationsAmount > 0 ? 100000 : 0,
      totalCurrentAmount: totalDonationsAmount,
      overallProgress: totalDonationsAmount > 0 ? Math.round((totalDonationsAmount / 100000) * 100) : 0
    };
  }, [donationGoals, publicSettings]);

  // ======================================================
  // DERIVED VALUES
  // ======================================================
  const completedProjectsCount = donationStatsData.completedGoals || 0;

  // ======================================================
  // FETCH DATA
  // ======================================================
  useEffect(() => {
    const fetchData = async () => {
      setIsPageLoading(true);
      setReviewsError(null);
      
      try {
        // Fetch all data in parallel for better performance
        await Promise.all([
          dispatch(getPublicSettings()).unwrap(),
          dispatch(getAllProviders()).unwrap(),
        ]);
      } catch (err) {
        console.error('Failed to fetch platform data:', err);
        if (err.message?.includes('providerId')) {
          setReviewsError('Reviews data requires a provider ID');
        }
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // ======================================================
  // PLATFORM INFO
  // ======================================================
  const platformInfo = {
    name: publicSettings?.platformInfo?.name || homeTitle,
    description: publicSettings?.platformInfo?.description || aboutDescription,
    mission: publicSettings?.platformInfo?.mission || missionText,
    vision: publicSettings?.platformInfo?.vision || visionText,
    contactEmail: publicSettings?.platformInfo?.contactEmail || 'contact@haitianservices.com',
    contactPhone: publicSettings?.platformInfo?.contactPhone || '+1 (800) 555-HAITI',
    address: publicSettings?.platformInfo?.address || {
      street: '123 Haitian Avenue',
      city: 'Port-au-Prince',
      state: 'Ouest',
      country: 'Haiti'
    }
  };

  const socialLinks = {
    facebook: publicSettings?.socialLinks?.facebook || 'https://facebook.com/haitianservices',
    instagram: publicSettings?.socialLinks?.instagram || 'https://instagram.com/haitianservices',
    twitter: publicSettings?.socialLinks?.twitter || 'https://twitter.com/haitianservices',
    youtube: publicSettings?.socialLinks?.youtube || 'https://youtube.com/haitianservices',
    linkedin: publicSettings?.socialLinks?.linkedin || 'https://linkedin.com/company/haitianservices'
  };

  // ======================================================
  // CORE VALUES
  // ======================================================
  const coreValues = [
    {
      icon: Heart,
      title: communityFirst,
      description: communityFirstDesc
    },
    {
      icon: Users,
      title: empowerment,
      description: empowermentDesc
    },
    {
      icon: Shield,
      title: trustSafety,
      description: trustSafetyDesc
    },
    {
      icon: Award,
      title: excellence,
      description: excellenceDesc
    }
  ];

  // ======================================================
  // FEATURES
  // ======================================================
  const features = [
    {
      icon: Globe,
      title: globalReach,
      description: globalReachDesc
    },
    {
      icon: Clock,
      title: availability,
      description: availabilityDesc
    },
    {
      icon: Users,
      title: communitySupport,
      description: communitySupportDesc
    }
  ];

  // ======================================================
  // LOADING STATE
  // ======================================================
  if (isPageLoading || settingsLoading || providersLoading || reviewsLoading || isAnalyzing) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>
          <Loader size={48} className={styles.spinnerIcon} />
          <p>Loading platform information...</p>
        </div>
      </div>
    );
  }

  // ======================================================
  // ERROR STATE
  // ======================================================
  if (settingsError) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <AlertCircle size={48} color="#dc3545" />
          <h2>Unable to Load Information</h2>
          <p>{settingsError}</p>
          <button 
            className={styles.retryButton}
            onClick={() => {
              setIsPageLoading(true);
              Promise.all([
                dispatch(getPublicSettings()).unwrap(),
                dispatch(getAllSettings()).unwrap(),
                dispatch(getAllProviders()).unwrap(),
              ])
                .catch(err => console.error('Failed to retry fetch:', err))
                .finally(() => setIsPageLoading(false));
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ======================================================
  // RENDER
  // ======================================================
  return (
    <div className={styles.aboutContainer}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeft size={24} color="#FFFFFF" />
          </button>
          <h1 className={styles.heroTitle}>{platformInfo.name}</h1>
          <p className={styles.heroDescription}>{platformInfo.description}</p>
          <div className={styles.heroButtons}>
            <Link to="/providers" className={styles.primaryBtn}>
              {exploreProviders}
              <ArrowRight size={18} />
            </Link>
            <Link to="/help-support" className={styles.secondaryBtn}>
              {contactUs}
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className={styles.statsSection}>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIconWrapper} style={{ background: 'rgba(231, 76, 60, 0.1)' }}>
              <Users size={24} color="#e74c3c" />
            </div>
            <h3 className={styles.statValue}>{providerStats.totalProviders || 0}</h3>
            <p className={styles.statLabel}>{activeProviders}</p>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statIconWrapper} style={{ background: 'rgba(52, 152, 219, 0.1)' }}>
              <Globe size={24} color="#3498db" />
            </div>
            <h3 className={styles.statValue}>{providerStats.countriesReached || 0}</h3>
            <p className={styles.statLabel}>{countriesReached}</p>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statIconWrapper} style={{ background: 'rgba(46, 204, 113, 0.1)' }}>
              <CheckCircle size={24} color="#2ecc71" />
            </div>
            <h3 className={styles.statValue}>{completedProjectsCount}</h3>
            <p className={styles.statLabel}>{completedProjectsLabel}</p>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statIconWrapper} style={{ background: 'rgba(241, 196, 15, 0.1)' }}>
              <ThumbsUp size={24} color="#f39c12" />
            </div>
            <h3 className={styles.statValue}>
              {satisfactionMetrics?.satisfactionRate || 0}%
            </h3>
            <p className={styles.statLabel}>{clientSatisfaction}</p>
            {satisfactionMetrics?.totalReviews > 0 && (
              <small className={styles.statSubtext}>
                Based on {satisfactionMetrics.totalReviews} review
                {satisfactionMetrics.totalReviews > 1 ? 's' : ''}
              </small>
            )}
          </div>
        </div>
      </div>

      {/* Satisfaction Breakdown Section (if we have reviews) */}
      {satisfactionMetrics?.totalReviews > 0 && satisfactionMetrics.totalReviews > 0 && (
        <div className={styles.satisfactionSection}>
          <div className={styles.satisfactionCard}>
            <h3 className={styles.satisfactionTitle}>Customer Satisfaction Breakdown</h3>
            <p className={styles.satisfactionSummary}>{satisfactionMetrics.summary}</p>
            
            <div className={styles.satisfactionBars}>
              <div className={styles.satisfactionBar}>
                <span className={styles.barLabel}>Positive</span>
                <div className={styles.barTrack}>
                  <div 
                    className={`${styles.barFill} ${styles.positive}`}
                    style={{ width: `${satisfactionMetrics.positivePercentage || 0}%` }}
                  />
                </div>
                <span className={styles.barValue}>{satisfactionMetrics.positivePercentage || 0}%</span>
              </div>
              
              <div className={styles.satisfactionBar}>
                <span className={styles.barLabel}>Neutral</span>
                <div className={styles.barTrack}>
                  <div 
                    className={`${styles.barFill} ${styles.neutral}`}
                    style={{ width: `${satisfactionMetrics.neutralPercentage || 0}%` }}
                  />
                </div>
                <span className={styles.barValue}>{satisfactionMetrics.neutralPercentage || 0}%</span>
              </div>
              
              <div className={styles.satisfactionBar}>
                <span className={styles.barLabel}>Negative</span>
                <div className={styles.barTrack}>
                  <div 
                    className={`${styles.barFill} ${styles.negative}`}
                    style={{ width: `${satisfactionMetrics.negativePercentage || 0}%` }}
                  />
                </div>
                <span className={styles.barValue}>{satisfactionMetrics.negativePercentage || 0}%</span>
              </div>
            </div>

            {/* Top Keywords */}
            {satisfactionMetrics.topPositiveKeywords && satisfactionMetrics.topPositiveKeywords.length > 0 && (
              <div className={styles.keywordSection}>
                <div className={styles.keywordGroup}>
                  <h4 className={styles.keywordTitle}>Top Positive Keywords</h4>
                  <div className={styles.keywordTags}>
                    {satisfactionMetrics.topPositiveKeywords.slice(0, 5).map((item, index) => (
                      <span key={index} className={`${styles.keywordTag} ${styles.positiveTag}`}>
                        {item.keyword} ({item.count})
                      </span>
                    ))}
                  </div>
                </div>
                
                {satisfactionMetrics.topNegativeKeywords && satisfactionMetrics.topNegativeKeywords.length > 0 && (
                  <div className={styles.keywordGroup}>
                    <h4 className={styles.keywordTitle}>Top Negative Keywords</h4>
                    <div className={styles.keywordTags}>
                      {satisfactionMetrics.topNegativeKeywords.slice(0, 5).map((item, index) => (
                        <span key={index} className={`${styles.keywordTag} ${styles.negativeTag}`}>
                          {item.keyword} ({item.count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={styles.satisfactionFooter}>
              <span className={styles.overallSentiment}>
                Overall Sentiment: <strong>{satisfactionMetrics.overallSentiment}</strong>
              </span>
              <span className={styles.reviewCount}>
                {satisfactionMetrics.totalReviews} reviews analyzed
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Mission & Vision */}
      <div className={styles.missionVisionSection}>
        <div className={styles.missionCard}>
          <div className={styles.missionIconWrapper}>
            <Target size={32} color={colors.primary} />
          </div>
          <h2 className={styles.sectionTitle}>{ourMission}</h2>
          <p className={styles.sectionText}>{platformInfo.mission}</p>
        </div>
        <div className={styles.visionCard}>
          <div className={styles.visionIconWrapper}>
            <Eye size={32} color={colors.secondary} />
          </div>
          <h2 className={styles.sectionTitle}>{ourVision}</h2>
          <p className={styles.sectionText}>{platformInfo.vision}</p>
        </div>
      </div>

      {/* Donation Goals Section */}
      <div className={styles.donationSection}>
        <div className={styles.donationHeader}>
          <h2>{donationGoalsCount}</h2>
          <div className={styles.donationStats}>
            <span className={styles.donationStatItem}>
              <DollarSign size={16} />
              ${donationStatsData.totalCurrentAmount.toLocaleString()} raised
            </span>
            <span className={styles.donationStatItem}>
              <Target size={16} />
              {donationStatsData.activeGoals} active goals
            </span>
            <span className={styles.donationStatItem}>
              <CheckCircle size={16} />
              {donationStatsData.completedGoals} completed
            </span>
          </div>
        </div>

        <div className={styles.donationGoalsList}>
          {donationGoals && donationGoals.length > 0 ? (
            donationGoals.map((goal, index) => {
              const targetAmount = Number(goal.targetAmount) || 0;
              const currentAmount = Number(goal.currentAmount) || 0;
              const progress = targetAmount > 0 ? (currentAmount / targetAmount) * 100 : 0;
              const isCompleted = targetAmount > 0 && currentAmount >= targetAmount;
              
              return (
                <div key={index} className={styles.goalProgressItem}>
                  <div className={styles.goalHeader}>
                    <span className={styles.goalTitle}>{goal.title}</span>
                    <div className={styles.goalAmount}>
                      <span className={styles.currentAmount}>
                        ${currentAmount.toLocaleString()}
                      </span>
                      <span className={styles.amountSeparator}> / </span>
                      <span className={styles.targetAmount}>
                        ${targetAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className={styles.goalProgressBar}>
                    <div 
                      className={`${styles.goalProgressFill} ${isCompleted ? styles.completed : ''}`}
                      style={{ 
                        width: `${Math.min(100, progress)}%`
                      }}
                    />
                  </div>
                  <span className={styles.goalProgressPercent}>{Math.round(progress)}%</span>
                </div>
              );
            })
          ) : (
            <p className={styles.noGoals}>No active donation goals at the moment.</p>
          )}
        </div>
      </div>

      {/* Core Values */}
      <div className={styles.valuesSection}>
        <h2 className={styles.valuesTitle}>{ourCoreValues}</h2>
        <p className={styles.valuesSubtitle}>{coreValuesSubtitle}</p>
        <div className={styles.valuesGrid}>
          {coreValues.map((value, index) => {
            const Icon = value.icon;
            return (
              <div key={index} className={styles.valueCard}>
                <div className={styles.valueIconWrapper}>
                  <Icon size={28} color={colors.primary} />
                </div>
                <h3 className={styles.valueTitle}>{value.title}</h3>
                <p className={styles.valueDescription}>{value.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Features */}
      <div className={styles.featuresSection}>
        <h2 className={styles.featuresTitle}>{whyChooseUs}</h2>
        <div className={styles.featuresGrid}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className={styles.featureCard}>
                <div className={styles.featureIconWrapper}>
                  <Icon size={24} color="#FFFFFF" />
                </div>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Contact & Social */}
      <div className={styles.contactSection}>
        <div className={styles.contactInfo}>
          <h2 className={styles.contactTitle}>{getInTouch}</h2>
          <div className={styles.contactDetails}>
            <div className={styles.contactItem}>
              <MapPin size={20} color={colors.primary} />
              <span>
                {platformInfo.address.street}, {platformInfo.address.city}
                <br />
                {platformInfo.address.state}, {platformInfo.address.country}
              </span>
            </div>
            <div className={styles.contactItem}>
              <Mail size={20} color={colors.primary} />
              <a href={`mailto:${platformInfo.contactEmail}`}>
                {platformInfo.contactEmail}
              </a>
            </div>
            <div className={styles.contactItem}>
              <Phone size={20} color={colors.primary} />
              <a href={`tel:${platformInfo.contactPhone}`}>
                {platformInfo.contactPhone}
              </a>
            </div>
          </div>
        </div>

        <div className={styles.socialSection}>
          <h3 className={styles.socialTitle}>{followUs}</h3>
          <div className={styles.socialLinks}>
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              aria-label="Follow us on Facebook"
            >
              <img 
                src="https://s.magecdn.com/social/tc-facebook.svg" 
                alt="Facebook" 
                width="20" 
                height="20" 
              />
              <span className={styles.socialLabel}>Facebook</span>
            </a>
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              aria-label="Follow us on Instagram"
            >
              <img 
                src="https://s.magecdn.com/social/tc-instagram.svg" 
                alt="Instagram" 
                width="20" 
                height="20" 
              />
              <span className={styles.socialLabel}>Instagram</span>
            </a>
            <a
              href={socialLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              aria-label="Follow us on Twitter"
            >
              <img 
                src="https://s.magecdn.com/social/tc-twitter.svg" 
                alt="Twitter" 
                width="20" 
                height="20" 
              />
              <span className={styles.socialLabel}>Twitter</span>
            </a>
            <a
              href={socialLinks.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              aria-label="Follow us on YouTube"
            >
              <img 
                src="https://s.magecdn.com/social/tc-youtube.svg" 
                alt="YouTube" 
                width="20" 
                height="20" 
              />
              <span className={styles.socialLabel}>YouTube</span>
            </a>
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              aria-label="Follow us on LinkedIn"
            >
              <img 
                src="https://s.magecdn.com/social/tc-linkedin.svg" 
                alt="LinkedIn" 
                width="20" 
                height="20" 
              />
              <span className={styles.socialLabel}>LinkedIn</span>
            </a>
          </div>
          <p className={styles.socialText}>{socialText}</p>
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboard}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div> 
          </div>

          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © {new Date().getFullYear()} {homeTitle}, {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;